/**
 * `usePaginatedFetcher` — drive a paginated `Page<T>` fetcher from React.
 *
 * Wraps the pure reducer in `paginatedReducer.ts` with a `useEffect` that
 * fires off requests when the in-flight page number changes.
 *
 * Behavior summary (matches `PaginatedGrid`'s docs):
 *   - Fetches page 1 on mount.
 *   - `loadMore()` schedules the next page if `hasMore && !loading && !error`.
 *   - On error, prior pages stay rendered; `retry()` clears the error so the
 *     next `loadMore()` can re-attempt the failing page.
 *   - When `resetKey` changes (e.g. source switch, query change), drops all
 *     state and re-fetches page 1.
 *   - Hard-caps at `MAX_PAGES = 100` to defend against sources that report
 *     `hasMore: true` indefinitely.
 *
 * Aborting: we don't AbortSignal the in-flight request on unmount — sources
 * don't all support cancellation, and the result of a request mid-flight
 * usually populates a parsing cache that's still useful next mount. We just
 * ignore the response (via the inFlightPage match check in the reducer).
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';

import type { Page } from '@/sources/types';

import {
  MAX_PAGES,
  flattenItems,
  hasMore as hasMoreSel,
  initialState,
  nextPageNumber,
  paginatedReducer,
  type PaginatedState,
} from './paginatedReducer';

export interface UsePaginatedFetcherOpts<T> {
  /**
   * Async fetcher invoked with a 1-indexed page number. Errors propagate
   * to the hook's `error` field — throw whatever type is convenient
   * (typically a {@link import('@/lib/errors').SourceError}).
   */
  fetcher: (page: number, cursor?: string) => Promise<Page<T>>;
  /**
   * When this value changes (by referential equality), the hook drops all
   * state and refetches page 1. Useful for source/search/category switches
   * where stale pages would confuse the user.
   */
  resetKey?: unknown;
  /**
   * Optional in-memory cache key for preserving loaded pages while the user
   * drills into a detail/reader route and comes back via app/browser back.
   */
  cacheKey?: string;
}

export interface UsePaginatedFetcherResult<T> {
  /** Flat list across every loaded page, in fetch order. */
  items: T[];
  /** Per-page snapshots — exposed in case a caller needs page metadata. */
  pages: Page<T>[];
  /** True while a fetch is in flight. */
  loading: boolean;
  /** Last fetch error, if any. Cleared by `retry()` or `reset()`. */
  error: unknown | null;
  /** True if a `loadMore()` would do something (see `hasMore` selector). */
  hasMore: boolean;
  /** The number of pages successfully loaded so far. */
  loadedPages: number;
  /** Trigger the next-page fetch. No-op if `!hasMore`. */
  loadMore: () => void;
  /** Clear `error` so a subsequent `loadMore()` retries the failed page. */
  retry: () => void;
  /** Wipe all loaded pages and refetch page 1. */
  reset: () => void;
}

const ROUTE_RESTORATION_CACHE_TTL_MS = 60 * 60 * 1000;
const CACHE_LIMIT = 40;

interface PageCacheEntry {
  savedAt: number;
  pages: Page<unknown>[];
}

const pageCache = new Map<string, PageCacheEntry>();

function readCachedPages<T>(cacheKey: string | undefined): Page<T>[] | undefined {
  if (!cacheKey) return undefined;
  const entry = pageCache.get(cacheKey);
  if (!entry) return undefined;
  if (Date.now() - entry.savedAt > ROUTE_RESTORATION_CACHE_TTL_MS) {
    pageCache.delete(cacheKey);
    return undefined;
  }
  // Refresh recency for the simple LRU trimming below.
  pageCache.delete(cacheKey);
  pageCache.set(cacheKey, entry);
  return entry.pages as Page<T>[];
}

function writeCachedPages<T>(cacheKey: string | undefined, pages: Page<T>[]) {
  if (!cacheKey || pages.length === 0) return;
  pageCache.set(cacheKey, { savedAt: Date.now(), pages: pages as Page<unknown>[] });
  while (pageCache.size > CACHE_LIMIT) {
    const oldest = pageCache.keys().next().value;
    if (oldest === undefined) break;
    pageCache.delete(oldest);
  }
}

export function usePaginatedFetcher<T>(
  opts: UsePaginatedFetcherOpts<T>,
): UsePaginatedFetcherResult<T> {
  const { fetcher, resetKey, cacheKey } = opts;
  const [state, dispatch] = useReducer(paginatedReducer<T>, undefined, () => {
    const cachedPages = readCachedPages<T>(cacheKey);
    return cachedPages
      ? {
          ...initialState<T>(),
          pages: cachedPages,
        }
      : initialState<T>();
  });

  // Stable refs for the latest fetcher + current state so the effect that
  // performs the actual fetch doesn't re-run on every prop change.
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  // Reset whenever resetKey changes. We deliberately don't merge this into
  // the data-fetch effect — the reducer's initial state is `inFlightPage:
  // null` so a stale in-flight response will be ignored on its own.
  const firstReset = useRef(true);
  useEffect(() => {
    if (firstReset.current) {
      firstReset.current = false;
      return;
    }
    const cachedPages = readCachedPages<T>(cacheKey);
    dispatch(cachedPages ? { type: 'restore', pages: cachedPages } : { type: 'reset' });
  }, [resetKey, cacheKey]);

  useEffect(() => {
    writeCachedPages(cacheKey, state.pages);
  }, [cacheKey, state.pages]);

  // Drive the actual network request whenever `inFlightPage` becomes a
  // number. The reducer ensures we don't end up in this state twice for
  // the same page; the cancelled-check defends against unmount.
  useEffect(() => {
    const page = state.inFlightPage;
    if (page === null) return;
    let cancelled = false;
    const cursor = state.inFlightCursor ?? undefined;
    fetcherRef
      .current(page, cursor)
      .then((p) => {
        if (cancelled) return;
        dispatch({ type: 'success', page: p });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        dispatch({ type: 'failure', error: err, page });
      });
    return () => {
      cancelled = true;
    };
  }, [state.inFlightPage, state.inFlightCursor]);

  // Auto-trigger the first page on mount and whenever a reset happens
  // (state.pages becomes empty again). We piggy-back on `loadMore` so the
  // reducer's gating rules (no double-load, no load over MAX_PAGES, etc.)
  // are exercised the same way as user-driven loads.
  const loadMore = useCallback(() => {
    dispatch({
      type: 'request',
      page: nextPageNumberFromState(state),
      cursor: nextCursorFromState(state),
    });
  }, [state]);

  // Bootstrap: when there are no pages, no in-flight request, no error,
  // request page 1. Stable across renders because `state` is the dep.
  useEffect(() => {
    if (
      state.pages.length === 0 &&
      state.inFlightPage === null &&
      state.error === null &&
      state.pages.length < MAX_PAGES
    ) {
      dispatch({ type: 'request', page: 1 });
    }
  }, [state.pages.length, state.inFlightPage, state.error]);

  const retry = useCallback(() => {
    dispatch({ type: 'retry' });
    // Kick off the next-page request immediately after the error is
    // cleared — the user clicked "Retry", they expect the network call.
    // We compute the next page inside the dispatch so we don't capture a
    // stale closure.
    dispatch({
      type: 'request',
      page: nextPageNumberFromState(state),
      cursor: nextCursorFromState(state),
    });
  }, [state]);

  const reset = useCallback(() => {
    dispatch({ type: 'reset' });
  }, []);

  return {
    items: flattenItems(state),
    pages: state.pages,
    loading: state.loading,
    error: state.error,
    hasMore: hasMoreSel(state),
    loadedPages: state.pages.length,
    loadMore,
    retry,
    reset,
  };
}

/**
 * Read the next-page number out of a `PaginatedState`. Kept inline (rather
 * than calling the public `nextPageNumber` selector) so we don't import
 * cycles when callers want to compose the hook with their own state.
 */
function nextPageNumberFromState<T>(state: PaginatedState<T>): number {
  return nextPageNumber(state);
}

function nextCursorFromState<T>(state: PaginatedState<T>): string | undefined {
  return state.pages[state.pages.length - 1]?.nextCursor;
}
